import _ from 'lodash';
import { useState, FC, useEffect } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import {
  FILTER_TO_KEY_PREFIX_MAP,
  MultiSelectionFilters,
} from '@components/Templates/TemplateGallery/FilterTags/useFilterTags';
import { useShallowRouter } from '@components/Hooks/useRouter';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { MultiFilterSelections } from '@components/Templates/FilterPanel/MultiSelectionFilter/MultiSelectionFilter';
import { updateQueryParams } from '@utils/route';
import MobileFilterModalLayout from '@components/MobileFilter/MobileFilterModalLayout';
import ControlledCheckboxGroup from '@components/Templates/FilterPanel/MultiSelectionFilter/ControlledCheckboxGroup';
import {
  getActiveMultiSelectionFiltersCount,
  getMultiFilterSelectionsFromIds,
  getQueryParamsFromMultiSelection,
  getSelectedValues,
} from '@components/Templates/FilterPanel/MultiSelectionFilter/multiSelectionFilterUtils';
import { IClearAllRecord, IOnClear } from '../TemplateMobileFilters';
import { FILTER_TO_TITLE_MAP } from '../TemplatesMobileFiltersMenu/TemplatesMobileFiltersMenu';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { useFiltersShouldClearSearch } from '@components/Hooks/useFiltersShouldClearSearch';

export const dataHooks = {
  filterScreen: (filterType: MultiSelectionFilters) =>
    `multi-selection-filter-screen-${filterType}`,
};

interface IMultiSelectionFilterScreenProps {
  onBack: () => void;
  onClose: () => void;
  items: Record<string, string>;
  filterType: MultiSelectionFilters;
  onClear: IOnClear;
  clearAllRecord: IClearAllRecord;
}

const MultiSelectionFilterScreen: FC<IMultiSelectionFilterScreenProps> = ({
  onBack,
  onClose,
  items,
  filterType,
  onClear,
  clearAllRecord,
}) => {
  const { t } = useTranslation();
  const { navigate, router } = useShallowRouter();
  const {
    state: { filters },
  } = useTemplatesContext();
  const { logBiClick } = useTemplatesBi({ is_mobile: true });
  const shouldClearSearch = useFiltersShouldClearSearch();

  const stateValue = filters[filterType];
  const wasCleared = clearAllRecord[filterType] || clearAllRecord.all;

  const [selections, setSelections] = useState<MultiFilterSelections>([]);

  const filterCount = getActiveMultiSelectionFiltersCount(selections);
  const onApply = () => {
    logBiClick({
      click_type: `${filterType} filter - Apply`,
      click_data: {
        filter_list: selections
          .filter((item) => item.isChecked)
          .map((item) => item.label),
      },
    });
    navigate(
      updateQueryParams(
        getQueryParamsFromMultiSelection(filterType, selections),
        router,
        { search: shouldClearSearch, ...clearAllRecord },
      ),
    );
    onClose();
  };

  const onClearClick = () => {
    onClear(filterType);
  };

  const onChangeSelection = (eventData: { id: string; checked: boolean }) => {
    const selectedValues = getSelectedValues(selections, eventData);
    logBiClick({
      click_type: `${eventData.checked ? 'mark' : 'unmark'} filter`,
      click_data: { filter_name: `${filterType} - ${eventData.id}` },
    });
    setSelections(selectedValues);
  };

  useEffect(() => {
    setSelections(
      getMultiFilterSelectionsFromIds({
        items,
        selectionIds: stateValue,
        wasCleared,
      }),
    );
  }, [items, stateValue, wasCleared]);

  return (
    <MobileFilterModalLayout
      title={t(FILTER_TO_TITLE_MAP[filterType])}
      dataHook={dataHooks.filterScreen(filterType)}
      selectedAmount={filterCount}
      onClear={onClearClick}
      onApply={onApply}
      onBack={onBack}
      content={
        <div
          style={{
            padding: '12px 24px 12px 24px',
          }}
        >
          <ControlledCheckboxGroup
            selections={selections}
            onChange={onChangeSelection}
            langTokenPrefix={FILTER_TO_KEY_PREFIX_MAP[filterType]}
            gapSize="24px"
            useShowMore
          />
        </div>
      }
    />
  );
};

export default MultiSelectionFilterScreen;
